import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Badge } from "@/components/ui";
import { Download, FileText, Presentation, MessageSquareQuote, DollarSign, ListChecks, PlayCircle } from "lucide-react";

async function downloadPdf(key, name) {
  try {
    const res = await api.get(`/docs/${key}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

const PRICE_STACK = [
  ["Implementation (one-time)", "$2.4M", "Setup, config, migration, training — charged once"],
  ["Base subscription", "$4.25 PMPM", "Core platform; drops with volume; upgrades + support included"],
  ["Upsell modules", "+$0.30–$0.85 PMPM", "AI, billing, contact center, interoperability, analytics"],
  ["Retainers (monthly)", "$7.5k–$18k/mo", "Config-as-a-service, CSM, compliance, reporting, BPO"],
  ["Alternative angles", "varies", "Value-based, per-transaction EDI, white-label, marketplace"],
];
const PITCH = [
  "The pain: care managers juggle 6 disconnected systems; audits are fire drills.",
  "The vision: one platform where AI drafts the care plan and letters for them.",
  "The proof: run the live demo / product tour.",
  "The economics: a few dollars per member replaces multiple vendors and lifts quality bonuses.",
  "The safety: HIPAA, SOC 2, NIST — and their data stays theirs.",
];

export default function SalesPlaybook() {
  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader title="Sales & Operator Playbook" subtitle="How to present, price, and sell Go MCO to Managed Care Organizations — plus the full monetization model."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadPdf("sales-playbook", "GoMCO-Sales-and-Operator-Playbook.pdf")} data-testid="download-salesplaybook-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href="/docs/GoMCO-Sales-and-Operator-Playbook.md" download><Button data-testid="download-salesplaybook"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>} />

      <Card className="overflow-hidden mb-6">
        <video controls playsInline className="w-full bg-brand-950" data-testid="playbook-video">
          <source src="/media/gomco-tour.mp4" type="video/mp4" />
        </video>
        <div className="px-5 py-3 flex items-center gap-2 text-sm text-muted-foreground border-t border-border">
          <PlayCircle className="h-4 w-4 text-brand-700" /> 60-second explainer — use it in demos, emails, and decks.
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Presentation className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">The Pitch (say it in this order)</h2></div>
          <ol className="space-y-2">
            {PITCH.map((p, i) => (
              <li key={i} className="flex gap-3 text-sm text-muted-foreground">
                <span className="h-5 w-5 shrink-0 rounded-full bg-brand-700 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{p}
              </li>
            ))}
          </ol>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><MessageSquareQuote className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">One-Sentence Pitch</h2></div>
          <p className="text-lg leading-relaxed text-foreground font-display">
            "Go MCO is the single platform that runs an MCO's entire clinical operation — care management,
            authorizations, quality, billing, and member engagement — with AI built in and security that passes
            any audit."
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["PMPM pricing", "Modular upsells", "White-label", "AI native"].map((t) => <Badge key={t} tone="Low">{t}</Badge>)}
          </div>
        </Card>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><DollarSign className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">What It Should Cost (the price stack)</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Revenue Line</th><th className="px-4 py-3 font-semibold">Price</th><th className="px-4 py-3 font-semibold">What it is</th></tr></thead>
            <tbody className="divide-y divide-border">
              {PRICE_STACK.map((r, i) => (
                <tr key={i} className="hover:bg-muted transition-colors">
                  <td className="px-4 py-3 font-medium">{r[0]}</td>
                  <td className="px-4 py-3 font-mono font-semibold text-brand-700">{r[1]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {[["Per member / month", "$4.25"], ["Year-1 (Trillium)", "$11.2M"], ["5-Year TCV", "~$69.5M"], ["Sub. gross margin", "75–80%"]].map(([l, v]) => (
            <div key={l} className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="text-xl font-bold font-display text-brand-700 mt-1">{v}</p></div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          <strong className="text-brand-700">Why PMPM, not per-user?</strong> Payers price by lives covered, not logins.
          If a buyer insists on per-user, the rough equivalent is <strong>$180–$260 per care-manager seat / month</strong> —
          but always steer to PMPM; it's larger and more predictable.
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4"><ListChecks className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Full Playbook & Companion Docs</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Download the complete guides. The Sales & Operator Playbook covers the RFP in plain English, the demo script, discovery questions, objection handling, and how to operate the platform.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[["sales-playbook", "Sales & Operator Playbook", "GoMCO-Sales-and-Operator-Playbook.pdf"],
            ["monetization", "Monetization Model", "GoMCO-Monetization-Model.pdf"],
            ["mco-playbook", "MCO Expansion Playbook", "GoMCO-MCO-Expansion-Playbook.pdf"]].map(([k, label, name]) => (
            <button key={k} onClick={() => downloadPdf(k, name)} className="text-left rounded-md border border-border p-4 hover:border-brand-500 hover:bg-brand-50 transition-colors">
              <FileText className="h-5 w-5 text-brand-700 mb-2" />
              <p className="text-sm font-semibold">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Download PDF</p>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
